/**
 * <cart-drawer>: native <dialog> drawer re-rendered through the Section Rendering API.
 * Listens for: cart:open, cart:close, cart:updated (detail.sections['<section-id>'] when present), cart:error.
 * Handles: [data-cart-open] links, [data-cart-close], [data-cart-change][data-line][data-quantity], gift note/attribute.
 */
class CartDrawer extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    const { signal } = this.#abort;
    this.sectionId = this.dataset.sectionId;

    document.addEventListener('cart:open', () => this.open(), { signal });
    document.addEventListener('cart:close', () => this.close(), { signal });
    document.addEventListener('cart:updated', (event) => this.#onUpdated(event), { signal });
    document.addEventListener('cart:error', (event) => this.#showError(event.detail && event.detail.message), { signal });

    // Header cart links (and any [data-cart-open]) open the drawer instead of navigating.
    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-cart-open]') : null;
      if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this.open(trigger);
    }, { signal });

    this.#bindInner();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get dialog() {
    return this.querySelector('dialog');
  }

  get inner() {
    return this.querySelector('[data-cart-drawer-inner]');
  }

  open(trigger) {
    const dialog = this.dialog;
    if (!dialog) return;
    this.lastTrigger = trigger instanceof HTMLElement ? trigger : document.activeElement;
    if (!dialog.open) {
      dialog.removeAttribute('data-closing');
      dialog.showModal();
      document.body.classList.add('has-modal-open');
      document.dispatchEvent(new CustomEvent('cart:opened'));
    }
    const first = this.querySelector('[data-focus-id="close"]');
    if (first instanceof HTMLElement) first.focus();
  }

  close() {
    const dialog = this.dialog;
    if (!dialog || !dialog.open) return;
    const finish = () => {
      dialog.removeAttribute('data-closing');
      dialog.close();
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
    } else {
      dialog.setAttribute('data-closing', '');
      dialog.addEventListener('animationend', finish, { once: true });
      setTimeout(() => dialog.hasAttribute('data-closing') && finish(), 400);
    }
  }

  #bindInner() {
    const { signal } = this.#abort;
    const dialog = this.dialog;
    if (!dialog) return;

    dialog.addEventListener('close', () => {
      document.body.classList.remove('has-modal-open');
      document.dispatchEvent(new CustomEvent('cart:closed'));
      if (this.lastTrigger instanceof HTMLElement && document.contains(this.lastTrigger)) this.lastTrigger.focus();
    }, { signal });

    // Click on the backdrop (dialog element itself) closes.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.close();
    }, { signal });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });

    dialog.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-cart-close]')) return this.close();
      const change = target.closest('[data-cart-change]');
      if (change instanceof HTMLElement) {
        this.#change(Number(change.dataset.line), Number(change.dataset.quantity), change.dataset.focusId);
      }
    }, { signal });

    // Gift flag + note are saved to the cart as attributes so they reach checkout.
    const saveGift = RM.debounce(() => this.#saveGift(), 500);
    dialog.addEventListener('input', (event) => {
      if (event.target instanceof Element && event.target.matches('[data-cart-note]')) saveGift();
    }, { signal });
    dialog.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.matches('[data-cart-gift]')) saveGift();
    }, { signal });
  }

  async #saveGift() {
    const gift = this.querySelector('[data-cart-gift]');
    const note = this.querySelector('[data-cart-note]');
    if (!gift || !note) return;
    try {
      await RM.fetchJSON(RM.config.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: gift.checked ? note.value : '',
          attributes: { 'Gift order': gift.checked ? 'Yes' : '' },
        }),
      });
    } catch (error) {
      this.#showError(error.message);
    }
  }

  async #change(line, quantity, focusId) {
    if (this.#busy || !line) return;
    this.#busy = true;
    this.inner?.setAttribute('aria-busy', 'true');
    try {
      const cart = await RM.fetchJSON(RM.config.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionId, sections_url: window.location.pathname }),
      });
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart, sections: cart.sections, source: 'cart-drawer', focusId },
      }));
    } catch (error) {
      this.#showError(error.message || RM.config.strings.cartError);
    } finally {
      this.#busy = false;
      this.inner?.removeAttribute('aria-busy');
    }
  }

  async #onUpdated(event) {
    const detail = event.detail || {};
    let html = detail.sections && detail.sections[this.sectionId];
    try {
      if (!html) html = await RM.fetchSection(this.sectionId);
    } catch (error) {
      return this.#showError(RM.config.strings.cartError);
    }
    this.#render(html, detail.focusId);
  }

  #render(html, focusId) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('[data-cart-drawer-inner]');
    const current = this.inner;
    if (!next || !current) return;
    const activeFocusId = focusId || (document.activeElement instanceof HTMLElement ? document.activeElement.dataset.focusId : '');
    current.innerHTML = next.innerHTML;
    if (activeFocusId) {
      const target = this.querySelector(`[data-focus-id="${CSS.escape(activeFocusId)}"]`) || this.querySelector('[data-focus-id="close"]');
      if (target instanceof HTMLElement) target.focus();
    }
  }

  #showError(message) {
    const box = this.querySelector('[data-cart-error]');
    if (!box) return;
    box.textContent = message || RM.config.strings.cartError;
    box.hidden = false;
    setTimeout(() => { box.hidden = true; }, 6000);
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
